import { format } from 'date-fns';

/**
 * This function is to evaluate when an item is defined
 * @param item the item to be evaluated
 * @returns true when item is defined, false otherwise
 */
export function itemIsNotNullAndNotUndefined(item: unknown): boolean {
	return item != undefined && item != null;
}

/**
 * This function is to evaluate when an item is not defined
 * @param item the item to be evaluated
 * @returns true if the item is not defined or is null, false otherwise
 */
export function itemIsNullOrUndefined(item: unknown): boolean {
	return item == undefined || item == null;
}

export const generatePagination = (currentPage: number, totalPages: number) => {
	const SHOW_TOTAL_PAGE_BUTTONS = 7;
	//hidden ellipsis
	if (totalPages <= SHOW_TOTAL_PAGE_BUTTONS) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}

	const FIRST_THREE_PAGES = 3;
	//show first 3 pages, ellipsis and last 2 pages
	if (currentPage <= FIRST_THREE_PAGES) {
		return [1, 2, 3, '...', totalPages - 1, totalPages];
	}

	const ANTEPENULTIMATE = 2;
	const PENULTIMATE = 1;
	//show the first 2 pages, ellipsis and the last 3 pages, when current pages is among last 3 pages.
	if (currentPage >= totalPages - ANTEPENULTIMATE) {
		return [
			1,
			2,
			'...',
			totalPages - ANTEPENULTIMATE,
			totalPages - PENULTIMATE,
			totalPages,
		];
	}

	// If the current page is somewhere in the middle,
	// show the first page, an ellipsis, the current page and its neighbors,
	// another ellipsis, and the last page.
	return [
		1,
		'...',
		currentPage - 1,
		currentPage,
		currentPage + 1,
		'...',
		totalPages,
	];
};

export const formatDateToUS = (dateString: string) => {
	const date = new Date(dateString);
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const year = date.getFullYear();
	return `${month}/${day}/${year}`;
};

export const formatPhoneNumber = (phoneNumber: string) => {
	if (phoneNumber) {
		const formattedPhone = phoneNumber
			.replace(/\s/g, '')
			.replaceAll('+1', '')
			.replaceAll('-', '')
			.replaceAll('(', '')
			.replaceAll(')', '');
		let formattedValue = '';
		if (formattedPhone.length === 10) {
			formattedValue = formattedPhone.replace(
				/([0-9]{3})([0-9]{3})([0-9]{4})/g,
				'+1 ($1) $2-$3'
			);
		}
		return formattedValue;
	}
};

export const getPercent = (currentStep: number, totalSteps: number) => {
	return Math.round((currentStep / totalSteps) * 100);
};

export const formatCurrency = (amount: string) => {
	const number = parseFloat(amount);
	if (isNaN(number)) {
		return 'Invalid number';
	}

	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

export const formatInputCurrency = (value: string | number) => {
	const amount = String(value);
	if (!amount) return '';

	const sanitizedAmount = amount.replace(/[^0-9.]/g, '');
	if (sanitizedAmount === '.') return '0.';

	const number = parseFloat(sanitizedAmount);

	if (isNaN(number)) {
		return sanitizedAmount;
	}

	const parts = sanitizedAmount.split('.');
	parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	return parts.join('.');
};

export const generateId = (): string => {
	const random = Math.random().toString(32).substring(2);
	const date = Date.now().toString(32);
	return random + date;
};

export const getNameAndInitialLastName = (username: string) => {
	if (!username) return '';
	const split = username.split(' ');
	const firstname = split[0];
	const initialLastName = split[1].charAt(0);
	return `${firstname} ${initialLastName}.`;
};

export const formatCurrencyAlt = (value: string, blur: any) => {
	// Get input value
	let inputValue = value?.toString();

	// Don't validate empty input
	if (inputValue === '') return '';

	// Check for decimal
	if (inputValue && inputValue?.indexOf('.') >= 0) {
		// Split number by decimal point
		const decimalPos = inputValue.indexOf('.');
		let leftSide = inputValue.substring(0, decimalPos);
		let rightSide = inputValue.substring(decimalPos);

		// Add commas to left side of number
		leftSide = formatNumber(leftSide);

		// Validate right side
		rightSide = rightSide.replace(/\D/g, '');

		// On blur, make sure two numbers after decimal
		if (blur === 'blur') {
			rightSide += '00';
		}

		// Limit decimal to two digits
		rightSide = rightSide.substring(0, 2);

		// Join number by "."
		inputValue = `${leftSide}.${rightSide}`;
	} else {
		// No decimal entered
		inputValue = formatNumber(inputValue);
		inputValue = `${inputValue}`;

		// Final formatting on blur
		if (blur === 'blur') {
			inputValue += '.00';
		}
	}

	return inputValue;
};

const formatNumber = (value: string) => {
	// Format number 1000000 to 1,234,567
	return value.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const getCurrentFormatDate = (): string => {
	const date = new Date();
	return format(date, 'MM/dd/yyyy');
};
